import React from 'react'

const preparationMethods = {
  english: [
    {
      title: 'Step‑by‑Step cultivation Process of Green Manuring',
      content: `1. Selection of the Crop
Choose fast‑growing legumes or nutrient‑rich plants:
Common options: Dhaincha, Sunhemp, Cowpea, Moong, Cluster bean, Lucerne, Moringa.
Select based on soil type, season, and target crop (e.g., dhaincha for paddy fields, cowpea for dryland).
2. Seed Preparation
Use recommended seed rate (per acre):
Dhaincha: 15–20 kg
Sunhemp: 8–10 kg
Cowpea: 20–25 kg
Moong: 8–12 kg
Moringa: 2–3 kg
Treat seeds with Rhizobium culture or biofertilizer before sowing → enhances nitrogen fixation
3. Sowing
Time: At onset of monsoon or with first irrigation.
Method: Broadcast or line sowing (20–25 cm spacing).
Depth: 2–3 cm for legumes.
Ensure adequate soil moisture for germination.
4. Growth Period
Allow crop to grow 45–60 days until flowering stage.
At this stage:
Maximum biomass production.
Nitrogen fixation is highest.
Plant tissues are tender → easy decomposition.

5. Incorporation into Soil
Plough or disc harrow the standing green manure crop.
Mix biomass thoroughly into moist soil.
Incorporate at least 15–20 days before sowing main crop to allow decomposition.


Irrigate lightly after incorporation to speed up breakdown.

🌱 Step‑by‑Step Biochar Preparation Method
1. Select Biomass
Common feedstocks: crop residues (rice husk, maize stalks, sugarcane bagasse), wood chips, coconut shells, animal manure.
Choose dry, clean material for efficient carbonization.
2. Drying
Biomass should be dried to reduce moisture (<20%).
Sun‑drying for 2–3 days is sufficient for most residues
3. Pyrolysis (Carbonization)
 Heat biomass in a low‑oxygen environment (to prevent complete burning).
 Temperature range: 300–600 °C.
 Methods:
 Traditional pit/earth kiln: Dig a pit, load biomass, cover with soil, allow slow smoldering.
 Metal drum kiln: Place biomass in a sealed drum with small air vents, ignite from below.
 Improved retort systems: More controlled, higher efficiency, less smoke
4. Cooling
 Once carbonization is complete, seal the chamber or cover with soil to cut off oxygen.
 Allow biochar to cool naturally before handling.
5. Crushing & Sieving
 Break biochar into small pieces or powder.
 Sieving ensures uniform particle size for soil application.
6. Activation/Enrichment (Optional)
 Mix biochar with compost, Jeevamrut, or liquid biofertilizers before application.
This “charges” biochar pores with nutrients and microbes, improving effectiveness
`
    },
    {
      title: 'How to Prepare Fermented Plant Extract',
      content: `Ingredients Required:
• Mixed fresh green leaves (Neem, Papaya, Guava, Moringa, Tulsi etc.) – 10–15 kg
• Jaggery – 1–2 kg
• Clean water – 150–200 litres
• A large drum or bucket
• A cloth for covering

Step-by-Step Preparation

Step 1 — Chop the Leaves
Cut mixed leaves into small 2–3 inch pieces.

Step 2 — Crush or Pound
Crush using:
• stone grinder
• wooden stick
• or mixer (for small quantity)
This helps release plant juice.

Step 3 — Add Leaves to Drum
Put all crushed leaves into a large drum.

Step 4 — Add Jaggery
Dissolve 1–2 kg jaggery in water and pour into the drum. This boosts fermentation.

Step 5 — Add Water
Add 150–200 litres of clean water.

Step 6 — Ferment the Mixture
• Cover with cloth (not airtight)
• Keep in shade
• Ferment 48 hours in summer, 72 hours in winter
• Stir once or twice daily
Foam/bubbles show active microbes.

Step 7 — Strain the Extract
After fermentation:
• Filter the liquid
• Remove leaf pulp
• Only liquid extract is used

✔️ How to Use
Soil Drench: Mix 10 L FPE + 90 L water and apply at root zone.
Foliar Spray: Mix 10 L FPE + 90 L water and spray on leaves.
Frequency: Every 20–25 days.`
    },
    {
      title: 'How to Prepare Jeevamrut',
      content: `Step 1 — Take a 200 L plastic drum
Fill drum with 180–190 L clean water (leave some space at top).

Step 2 — Add Cow Dung (10 kg)
Break the dung into small pieces and mix well in the water.

Step 3 — Add Cow Urine (10 L)
Pour slowly and mix with a wooden stick.

Step 4 — Add Jaggery (2 kg)
Dissolve separately in a small bucket of water → pour into drum.

Step 5 — Add Besan (2 kg)
Add besan slowly while stirring to avoid lumps.

Step 6 — Add 1–2 handful Soil from your farm
Prefer soil which is not exposed to chemicals.
This contains local beneficial microbes.

Step 7 — Mix everything thoroughly
Stir clockwise for 2–3 minutes.

Step 8 — Leave the mixture for fermentation (किण्वन)
• Keep in shade
• Cover with a cloth (not airtight)
• Ferment for 48 hours in summer, 72 hours in winter
• Stir twice a day
• You will notice froth and smell — this means microbes are active.

Your Jeevamrut is Ready!

✔️ How to Use
Soil Drench: 100–200 L/acre (depending on crop)
Foliar Spray: Mix 5 L Jeevamrut + 95 L water
Use every 15–20 days
Do not use under hot afternoon sun`
    }
  ],
  hindi: [
    {
      title: 'हरी खाद की चरणबद्ध खेती प्रक्रिया',
      content: `1. फसल का चयन
तेज़ी से बढ़ने वाली फलियाँ या पोषक तत्वों से भरपूर पौधे चुनें:
सामान्य विकल्प: ढैंचा, सनई, लोबिया, मूंग, क्लस्टर बीन, ल्यूसर्न, मोरिंगा।
मिट्टी के प्रकार, मौसम और लक्षित फसल (जैसे, धान के खेतों के लिए ढैंचा, शुष्क भूमि के लिए लोबिया) के आधार पर चयन करें।
2. बीज तैयार करना
अनुशंसित बीज दर (प्रति एकड़) का प्रयोग करें:
ढैंचा: 15-20 कि.ग्रा.
सनई: 8-10 कि.ग्रा.
लोबिया: 20-25 कि.ग्रा.
मूंग: 8-12 कि.ग्रा.
मोरिंगा: 2-3 कि.ग्रा.
बुवाई से पहले बीजों को राइज़ोबियम कल्चर या जैवउर्वरक से उपचारित करें → नाइट्रोजन स्थिरीकरण को बढ़ाता है।
3. बुवाई
समय: मानसून की शुरुआत में या पहली सिंचाई के साथ।
विधि: छिटककर या कतार में बुवाई (20-25 सेमी की दूरी पर)।
गहराई: फलियों के लिए 2-3 सेमी।
अंकुरण के लिए पर्याप्त मिट्टी की नमी सुनिश्चित करें।
4. वृद्धि अवधि
फूल आने तक फसल को 45-60 दिनों तक बढ़ने दें।
इस अवस्था में:
अधिकतम बायोमास उत्पादन।
नाइट्रोजन स्थिरीकरण सबसे अधिक होता है।
पौधों के ऊतक कोमल होते हैं → आसानी से अपघटन।

5. मिट्टी में मिलाना
खड़ी हरी खाद वाली फसल की जुताई या डिस्क हैरो से करें।
बायोमास को नम मिट्टी में अच्छी तरह मिलाएँ।
मुख्य फसल की बुवाई से कम से कम 15-20 दिन पहले मिलाएँ ताकि अपघटन हो सके।

अपघटन की गति बढ़ाने के लिए मिलाने के बाद हल्की सिंचाई करें।

🌱 चरण-दर-चरण बायोचार तैयार करने की विधि
1. बायोमास चुनें
सामान्य फीडस्टॉक: फसल अवशेष (चावल की भूसी, मक्के के डंठल, गन्ने की खोई), लकड़ी के चिप्स, नारियल के खोल, पशु खाद।
कुशल कार्बनीकरण के लिए सूखी, साफ़ सामग्री चुनें।
2. सुखाना
नमी (<20%) कम करने के लिए बायोमास को सुखाया जाना चाहिए।
अधिकांश अवशेषों के लिए 2-3 दिनों तक धूप में सुखाना पर्याप्त है।
3. पायरोलिसिस (कार्बोनाइजेशन)
बायोमास को कम ऑक्सीजन वाले वातावरण में गर्म करें (पूरी तरह जलने से बचाने के लिए)।
तापमान सीमा: 300-600 °C।
विधियाँ:
पारंपरिक गड्ढा/मिट्टी का भट्ठा: एक गड्ढा खोदें, बायोमास भरें, मिट्टी से ढक दें, और धीमी सुलगने दें।
धातु ड्रम भट्ठा: बायोमास को छोटे वायु वेंट वाले एक सीलबंद ड्रम में रखें, नीचे से प्रज्वलित करें।
बेहतर रिटॉर्ट सिस्टम: अधिक नियंत्रित, उच्च दक्षता, कम धुआँ।
4. ठंडा करना
कार्बनीकरण पूरा होने के बाद, ऑक्सीजन को रोकने के लिए कक्ष को सील कर दें या मिट्टी से ढक दें।
बायोचार को संभालने से पहले प्राकृतिक रूप से ठंडा होने दें।
5. कुचलना और छानना
बायोचार को छोटे टुकड़ों या पाउडर में तोड़ लें।
छनाई से मिट्टी में एक समान कण आकार सुनिश्चित होता है।
6. सक्रियण/संवर्धन (वैकल्पिक)
बायोचार को इस्तेमाल से पहले कम्पोस्ट, जीवामृत या तरल जैवउर्वरकों के साथ मिलाएँ।
इससे बायोचार के छिद्र पोषक तत्वों और सूक्ष्मजीवों से ""आवेशित"" हो जाते हैं, जिससे प्रभावशीलता बढ़ जाती है।
`
    },
    {
      title: 'किण्वित पौधे का अर्क कैसे तैयार करें',
      content: `आवश्यक सामग्री:
• मिश्रित ताजी हरी पत्तियां (नीम, पपीता, अमरूद, मोरिंगा, तुलसी आदि) – 10–15 kg
• गुड़ – 1–2 kg
• साफ पानी – 150–200 लीटर
• एक बड़ा ड्रम या बाल्टी
• ढकने के लिए कपड़ा

चरण-दर-चरण तैयारी

चरण 1 — पत्तियों को काटें
मिश्रित पत्तियों को छोटे 2–3 इंच टुकड़ों में काटें।

चरण 2 — कुचलें या पीसें
इनसे कुचलें:
• पत्थर की चक्की
• लकड़ी की छड़ी
• या मिक्सर (छोटी मात्रा के लिए)
यह पौधे के रस को निकालने में मदद करता है।

चरण 3 — पत्तियों को ड्रम में डालें
सभी कुचली हुई पत्तियों को एक बड़े ड्रम में डालें।

चरण 4 — गुड़ डालें
1–2 kg गुड़ को पानी में घोलें और ड्रम में डालें। यह किण्वन को बढ़ावा देता है।

चरण 5 — पानी डालें
150–200 लीटर साफ पानी डालें।

चरण 6 — मिश्रण को किण्वित करें
• कपड़े से ढकें (एयरटाइट नहीं)
• छाया में रखें
• गर्मी में 48 घंटे, सर्दी में 72 घंटे किण्वित करें
• दिन में एक या दो बार हिलाएं
झाग/बुलबुले सक्रिय सूक्ष्मजीव दिखाते हैं।

चरण 7 — अर्क को छानें
किण्वन के बाद:
• तरल को छानें
• पत्ती का गूदा हटा दें
• केवल तरल अर्क का उपयोग किया जाता है

✔️ कैसे उपयोग करें
मिट्टी ड्रेंच: 10 L FPE + 90 L पानी मिलाएं और जड़ क्षेत्र में लगाएं।
पर्णीय स्प्रे: 10 L FPE + 90 L पानी मिलाएं और पत्तियों पर स्प्रे करें।
आवृत्ति: हर 20–25 दिन।`
    },
    {
      title: 'जीवामृत कैसे तैयार करें',
      content: `चरण 1 — 200 L प्लास्टिक ड्रम लें
ड्रम को 180–190 L साफ पानी से भरें (ऊपर कुछ जगह छोड़ें)।

चरण 2 — गाय का गोबर डालें (10 kg)
गोबर को छोटे टुकड़ों में तोड़ें और पानी में अच्छी तरह मिलाएं।

चरण 3 — गाय का मूत्र डालें (10 L)
धीरे-धीरे डालें और लकड़ी की छड़ी से मिलाएं।

चरण 4 — गुड़ डालें (2 kg)
अलग से एक छोटी बाल्टी पानी में घोलें → ड्रम में डालें।

चरण 5 — बेसन डालें (2 kg)
गांठ से बचने के लिए हिलाते हुए धीरे-धीरे बेसन डालें।

चरण 6 — अपने खेत से 1–2 मुट्ठी मिट्टी डालें
ऐसी मिट्टी का उपयोग करें जो रसायनों के संपर्क में न आई हो।
इसमें स्थानीय लाभकारी सूक्ष्मजीव होते हैं।

चरण 7 — सब कुछ अच्छी तरह मिलाएं
2–3 मिनट के लिए दक्षिणावर्त हिलाएं।

चरण 8 — मिश्रण को किण्वन के लिए छोड़ दें (किण्वन)
• छाया में रखें
• कपड़े से ढकें (एयरटाइट नहीं)
• गर्मी में 48 घंटे, सर्दी में 72 घंटे किण्वित करें
• दिन में दो बार हिलाएं
• आप झाग और गंध देखेंगे — इसका मतलब है कि सूक्ष्मजीव सक्रिय हैं।

आपका जीवामृत तैयार है!

✔️ कैसे उपयोग करें
मिट्टी ड्रेंच: 100–200 L/एकड़ (फसल के आधार पर)
पर्णीय स्प्रे: 5 L जीवामृत + 95 L पानी मिलाएं
हर 15–20 दिन में उपयोग करें
गर्म दोपहर की धूप में उपयोग न करें`
    }
  ]
}

export default function Technic({ language = 'english' }) {
  const lang = language === 'hindi' ? 'hindi' : 'english'
  const methods = preparationMethods[lang]

  return (
    <div style={{ marginTop: '20px', marginBottom: '20px' }}>
      {/* Header Section */}
      <div style={{
        backgroundColor: '#134225',
        color: 'white',
        padding: '10px 14px',
        fontSize: '15px',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '0'
      }}>
        {lang === 'hindi' ? 'तैयारी तकनीक' : 'PREPARATION TECHNIQUES'}
      </div>
      
      {/* Three Column Layout */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '2px',
        marginTop: '0',
        backgroundColor: '#dee2e6'
      }}>
        {methods.map((method, index) => (
          <div key={index} style={{
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Column Header */}
            <div style={{
              backgroundColor: '#134225',
              color: 'white',
              padding: '10px 12px',
              fontSize: '12px',
              fontWeight: '700',
              textAlign: 'center',
              borderBottom: '2px solid #0d2e18'
            }}>
              {method.title}
            </div>
            
            {/* Column Content */}
            <div style={{
              padding: '12px',
              fontSize: '10px',
              lineHeight: '1.5',
              color: '#333',
              whiteSpace: 'pre-line',
              flex: 1
            }}>
              {method.content}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
